// src/pages/Auditorias/components/ConteudoDialog.js
import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  obterConteudoPeca, obterArtefacto, descarregarFicheiro, urlOriginalOneDrive
} from '../api';
import {
  GRAVIDADE_CLASSE, GRAVIDADE_LABEL, formatarTamanho, formatarData
} from '../utils/formatacao';

/**
 * Visualizador de um ficheiro da auditoria.
 *
 * alvo = { tipo: 'artefacto'|'avaliacao'|'transcricao', refId, titulo }
 *
 * Numa peça, alterna entre a avaliação e a transcrição integral sem fechar o
 * diálogo — ver a avaliação sem poder confrontá-la com o texto que a originou
 * obrigava a fechar, procurar na árvore e reabrir.
 */
const ConteudoDialog = ({ auditoriaId, alvo, onClose, onAnotar }) => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [vista, setVista] = useState(alvo?.tipo || 'avaliacao');
  const [nota, setNota] = useState('');
  const [notaEstado, setNotaEstado] = useState(null);

  const ePeca = alvo && alvo.tipo !== 'artefacto';

  const carregar = useCallback((qual) => {
    setCarregando(true);
    const pedido = alvo.tipo === 'artefacto'
      ? obterArtefacto(auditoriaId, alvo.refId)
      : obterConteudoPeca(auditoriaId, alvo.refId, qual);

    pedido
      .then(({ data }) => {
        setDados(data.data);
        setNota(data.data.nota_revisao || '');
        setErro(null);
      })
      .catch((e) => setErro(e.response?.data?.message || 'Erro ao carregar o ficheiro'))
      .finally(() => setCarregando(false));
  }, [auditoriaId, alvo]);

  useEffect(() => {
    if (!alvo) return;
    setVista(alvo.tipo);
    carregar(alvo.tipo);
  }, [alvo, carregar]);

  if (!alvo) return null;

  const eTranscricao = vista === 'transcricao';
  const conteudo = dados?.conteudo || '';

  const mudarVista = (qual) => {
    if (qual === vista) return;
    setVista(qual);
    carregar(qual);
  };

  const copiar = () => {
    if (!conteudo) return;
    navigator.clipboard.writeText(conteudo)
      .then(() => alert('Conteúdo copiado'))
      .catch((e) => console.error('Erro ao copiar:', e));
  };

  const descarregar = () => {
    descarregarFicheiro(auditoriaId, vista, alvo.refId, dados?.nome_ficheiro)
      .catch((e) => console.error('Erro no download:', e));
  };

  const abrirOriginal = async () => {
    if (!dados?.caminho_onedrive) return;
    try {
      const { data } = await urlOriginalOneDrive(dados.caminho_onedrive);
      // O controller devolve { success, downloadUrl, nome, tamanho } — mesmo
      // formato lido pelo ClientData/MovementsTable, que é o que está provado.
      if (data.success && data.downloadUrl) {
        window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert(data.message || 'Não foi possível obter o URL do ficheiro original');
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Erro ao abrir o original no OneDrive');
    }
  };

  const gravarNota = async () => {
    setNotaEstado('a guardar');
    try {
      await onAnotar(alvo.refId, nota);
      setNotaEstado('guardada');
      setTimeout(() => setNotaEstado(null), 2500);
    } catch (e) {
      setNotaEstado('erro ao guardar');
    }
  };

  return (
    <div className="aud-overlay" onClick={onClose}>
      <div className="aud-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aud-modal-cabecalho">
          <h3>{dados?.nome_ficheiro || alvo.titulo}</h3>
          <button className="aud-modal-fechar" onClick={onClose} aria-label="Fechar">✖</button>
        </div>

        <div className="aud-modal-corpo">
          {erro && <p className="aud-erro">{erro}</p>}

          {dados && (
            <>
              <div className="aud-meta">
                {dados.nome_original && <span>{dados.nome_original}</span>}
                {dados.data_movimento && <span>{formatarData(dados.data_movimento)}</span>}
                {dados.etapa_processual && <span>{dados.etapa_processual}</span>}
                {dados.tipo_documento && <span>{dados.tipo_documento}</span>}
                {dados.gravidade && (
                  <span className={`aud-badge ${GRAVIDADE_CLASSE[dados.gravidade] || ''}`}>
                    {GRAVIDADE_LABEL[dados.gravidade] || dados.gravidade}
                  </span>
                )}
              </div>

              {dados.nome_ficheiro_original && (
                <div className="aud-ficheiro-original">
                  <span className="aud-mono" title={dados.caminho_onedrive}>
                    {dados.nome_ficheiro_original}
                  </span>
                  <button type="button" className="aud-link" onClick={abrirOriginal}>
                    abrir PDF original
                  </button>
                </div>
              )}

              {ePeca && (
                <div className="aud-abas">
                  <button
                    type="button"
                    className={vista === 'avaliacao' ? 'activa' : ''}
                    onClick={() => mudarVista('avaliacao')}
                  >
                    Avaliação
                    {dados.chars_avaliacao ? ` · ${formatarTamanho(dados.chars_avaliacao)}` : ''}
                  </button>
                  <button
                    type="button"
                    className={vista === 'transcricao' ? 'activa' : ''}
                    onClick={() => mudarVista('transcricao')}
                    disabled={!dados.chars_transcricao}
                  >
                    Transcrição integral
                    {dados.chars_transcricao ? ` · ${formatarTamanho(dados.chars_transcricao)}` : ''}
                  </button>
                </div>
              )}

              {dados.conteudo_esperado && !eTranscricao && (
                <div className="aud-esperado">
                  <strong>Esperava-se nesta peça:</strong> {dados.conteudo_esperado}
                </div>
              )}

              {carregando && <p className="aud-vazio">A carregar…</p>}

              {!carregando && !conteudo && (
                <p className="aud-vazio">
                  {dados.erro || 'Sem conteúdo disponível para este ficheiro.'}
                </p>
              )}

              {!carregando && conteudo && (eTranscricao
                ? <pre className="aud-transcricao">{conteudo}</pre>
                : (
                  <div className="aud-markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{conteudo}</ReactMarkdown>
                  </div>
                )
              )}

              {ePeca && onAnotar && (
                <div className="aud-nota">
                  <label htmlFor="aud-nota-campo">
                    Nota de revisão
                    <span className="aud-sub"> — para afinar os prompts; não entra no parecer</span>
                  </label>
                  <textarea
                    id="aud-nota-campo"
                    rows={3}
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    placeholder="Ex.: devia ser Grave — a falta do AR impede contar o prazo"
                  />
                  <div className="aud-nota-accoes">
                    <button type="button" className="aud-btn-secundario" onClick={gravarNota}>
                      GUARDAR NOTA
                    </button>
                    {notaEstado && <span className="aud-sub">{notaEstado}</span>}
                    {dados.nota_por && !notaEstado && (
                      <span className="aud-sub">
                        por {dados.nota_por} em {formatarData(dados.nota_em)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {conteudo && (
          <div className="aud-modal-rodape">
            <button className="aud-btn-primario" onClick={copiar}>COPIAR</button>
            <button className="aud-btn-secundario" onClick={descarregar}>DESCARREGAR .TXT</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConteudoDialog;
